/**
 * Error Handler Utilities
 * Standardized error handling for API errors with toast notifications
 *
 * WHEN TO USE THIS FILE:
 * - Generic API calls (CRUD operations, form submissions)
 * - Most mutation error handlers
 * - Simple "show error and log" scenarios
 *
 * WHEN TO USE helpers/services/error-handler.ts:
 * - Cart operations (handleAddToCartError)
 * - Checkout flow (handleCheckoutError)
 * - Promo code validation (handlePromoCodeError)
 * - Account operations (handleAccountError)
 *
 * These specialized handlers have complex logic for handling
 * HTTP status codes (409 conflicts, 404 not found) with
 * appropriate redirects and retry behaviors.
 */

import { toast } from 'sonner'
import { ApiError } from '@/services/shared/client'
import { logger } from '@/lib/logger'

/**
 * API Error with backend validation details
 */
interface ApiValidationError {
  message: string
  errorId?: string
  isUserError?: boolean
  status?: number
}

/**
 * Handle API errors with user-friendly toast notifications
 * Shows backend validation messages and only logs non-user errors
 *
 * @param error - The error object from API call
 * @param fallbackMessage - Optional fallback message if error has no message
 * @param context - Optional context string for logging (e.g., "Save", "Publish")
 * @param debug - Set to true to log all errors including user validation errors (default: false)
 * @returns void
 *
 * @example
 * ```typescript
 * try {
 *   await updateMutation.mutateAsync({ id, data })
 *   toast.success('Saved successfully!')
 * } catch (error) {
 *   handleApiError(error, 'Failed to save', 'Save')
 * }
 * ```
 */
export function handleApiError(
  error: unknown,
  fallbackMessage = 'An error occurred. Please try again.',
  context?: string,
  debug = false
): void {
  // Handle ApiError instances
  if (error instanceof ApiError) {
    toast.error(error.message)

    // Always log system errors (5xx) to Sentry
    // User errors (4xx) are expected validation errors - only log in debug mode
    if (!error.isUserError || debug) {
      logger.error(`[${context || 'API'}] Error:`, {
        message: error.message,
        errorId: error.errorId,
        status: error.status,
        isUserError: error.isUserError,
        data: error.data,
      })
    }
    return
  }

  // Handle generic objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    const apiError = error as ApiValidationError

    // Show the backend error message (user-friendly validation message)
    toast.error(apiError.message)

    // Always log system errors (5xx) to Sentry
    const isSystemError = apiError.status && apiError.status >= 500
    if (isSystemError || debug) {
      logger.error(`[${context || 'API'}] Error:`, {
        message: apiError.message,
        errorId: apiError.errorId,
        status: apiError.status,
        isUserError: apiError.isUserError,
        error,
      })
    }
    return
  }

  // Handle Error instances (unexpected errors - always log to Sentry)
  if (error instanceof Error) {
    toast.error(error.message || fallbackMessage)
    logger.error(`[${context || 'API'}] Unexpected error:`, error)
    return
  }

  // Generic error fallback (unexpected - always log to Sentry)
  toast.error(fallbackMessage)
  logger.error(`[${context || 'API'}] Unknown error:`, error)
}

/**
 * Format error ID for display to users
 * Useful for support tickets
 *
 * @param errorId - The error ID from backend
 * @returns Formatted string for display
 *
 * @example
 * ```typescript
 * const errorId = 'aBcDeFgHiJ'
 * logger.log(formatErrorId(errorId)) // "Error ID: aBcDeFgHiJ"
 * ```
 */
export function formatErrorId(errorId?: string): string {
  if (!errorId) return ''
  return `Error ID: ${errorId}`
}

/**
 * Show error toast with optional error ID for support
 *
 * @param message - The error message to display
 * @param errorId - Optional error ID from backend
 *
 * @example
 * ```typescript
 * showErrorWithId(
 *   'Lock mode requires a passcode',
 *   'aBcDeFgHiJ'
 * )
 * // Shows: "Lock mode requires a passcode (Error ID: aBcDeFgHiJ)"
 * ```
 */
export function showErrorWithId(message: string, errorId?: string): void {
  const errorIdText = errorId ? ` (${formatErrorId(errorId)})` : ''
  toast.error(`${message}${errorIdText}`)
}
